/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { API, graphqlOperation } from "aws-amplify";
import {
  getAccountMini,
  getProductTotals,
} from "../../../graphql/account/account-queries";

import { Backdrop, CircularProgress } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  Button,
  Edit,
  FormTab,
  SaveButton,
  TabbedForm,
  Toolbar,
  TopToolbar,
  useEditController,
  useNotify,
} from "react-admin";
import { useForm, useFormState } from "react-final-form";
import setFieldData from "final-form-set-field-data";

import { CopyIcon } from "../../../components/common/iconography";
import { BackButton } from "../../../components/common/BackButton";
import { getCustomFieldsToSave } from "../../../components/common/CustomFields";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { HelpButton } from "../../../components/common/HelpButton";
import { HistoryTab } from "../../../components/common/HistoryTab";

import { getEnv, logError } from "../../../utils/appUtils";
import {
  InventoryStatus,
  ResourceType,
  Unit,
  UnitOptions,
} from "../../../utils/constants";
import {
  autogenHistoryNotes,
  deepRedirect,
  getBackPath,
} from "../../../utils/utils";

import { ProductEditHelp } from "./ProductHelp";
import { ProductForm } from "./ProductForm";
import { ProductEditAside } from "./ProductEditAside";
import { DeleteInactivateButton } from "./DeleteInactivateButton";
import { computeLowInventoryAlert, handleValidateSave } from "./ProductUtils";
import { AgentConfigForm } from "./AgentConfigForm";
import { TCFTablesForm } from "./TCFTablesForm";
import { sanitizeProductEdit } from "../../../graphql/account/accountCRUD";
import { isLiquidBulkCommodity } from "../../../utils/loadTypeUtils";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
  },
}));

const fetchAccount = async (accountId, setAccount, notify) => {
  try {
    const resp = await API.graphql(
      graphqlOperation(getAccountMini, { id: accountId }),
    );
    setAccount(resp.data.getAccount);
  } catch (error) {
    logError("product:ProductEdit:fetchAccount", error, notify);
  }
};

// The form's totals are as old as the page; the lambda may have moved them
// since. Read what is on hand now before deciding the low-inventory flag.
const fetchProductTotals = async (productId) => {
  const resp = await API.graphql(
    graphqlOperation(getProductTotals, { id: productId }),
  );
  return resp.data.getProduct || {};
};

const EditToolbar = ({ record, backPath, history, account, ...props }) => {
  const form = useForm();
  const notify = useNotify();
  const classes = useStyles();
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const transform = async (data) => {
    setIsSaving(true);
    debugger;
    data.status = InventoryStatus.STORED; // used for inventories connection

    const unitEntries = UnitOptions.filter(
      (e) => e.id !== Unit.CAR && e.id !== Unit.TL,
    );

    data.height = parseFloat(data["height"]);
    data.width = parseFloat(data["width"]);
    data.length = parseFloat(data["length"]);

    data.pcsHeight = parseFloat(data["pcsHeight"]);
    data.pcsWidth = parseFloat(data["pcsWidth"]);
    data.pcsLength = parseFloat(data["pcsLength"]);

    unitEntries.forEach((unit) => {
      data[unit.column] = parseFloat(data[unit.column]);
    });

    let lowInventoryAlert;
    try {
      const liveTotals = await fetchProductTotals(data.id);
      lowInventoryAlert = computeLowInventoryAlert(
        liveTotals,
        data.lowInventoryThreshold,
      );

      // autogen logHistory
      autogenHistoryNotes(form, data.id, ResourceType.PRODUCT);

      data.customFields = getCustomFieldsToSave(
        data.customFields,
        account.inventoryCustomFields,
      );

      // update all inventories for this product with the new productCode / loadType
      if (
        data.productCode !== record.productCode ||
        data.reloadYardCommodityId !== record.reloadYardCommodityId
      ) {
        // processProduct reads only these four; it never writes the Product
        // row, so nothing else — least of all the totals — belongs here.
        const response = await API.put("transREST", "/productUpdate", {
          body: {
            productUpdateInput: {
              id: data.id,
              reloadYardId: data.reloadYardId,
              productCode: data.productCode,
              reloadYardCommodityId: data.reloadYardCommodityId,
            },
            user: window.appUser.email,
            reloadYardId: window.appUser.reloadYardId,
          },
        });
        if (response.error) throw response.error;
      }
    } catch (error) {
      logError("product:ProductEdit:transform", error, notify);
      return Promise.reject();
    }

    data = sanitizeProductEdit(data);
    data.lowInventoryAlert = lowInventoryAlert;

    return data;
  };

  const onSuccess = async () => {
    notify(`Product updated successfully.`);
    deepRedirect(history, backPath);
    setIsSaving(false);
  };

  const onFailure = (error) => {
    setIsSaving(false);
    logError("product:ProductEdit:onFailure", error, notify);
  };

  const isPristine = () => {
    const formState = form.getState();
    return formState.pristine;
  };

  return (
    <Toolbar {...props} style={{ justifyContent: "space-between" }}>
      <Backdrop className={classes.backdrop} open={isSaving || isDeleting}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <SaveButton
        label="Save"
        disabled={isPristine() || isDeleting}
        saving={isSaving}
        handleSubmitWithRedirect={handleValidateSave(
          form,
          setIsSaving,
          props.handleSubmitWithRedirect,
          notify,
        )}
        transform={transform}
        onSuccess={onSuccess}
        onFailure={onFailure}
      />
      <DeleteInactivateButton
        record={record}
        history={history}
        backPath={backPath}
        isSaving={isSaving}
        isDeleting={isDeleting}
        setIsDeleting={setIsDeleting}
      />
    </Toolbar>
  );
};

const CloneButton = ({ history, backPath, account, data, ...props }) => {
  const handleClone = () => {
    deepRedirect(history, "/products/create", backPath, {
      account: account,
      clone: data,
    });
  };

  return (
    <Button label="Clone" size="small" color="primary" onClick={handleClone}>
      <CopyIcon />
    </Button>
  );
};

const EditActions = (props) => {
  return (
    <TopToolbar>
      <HelpButton>
        <ProductEditHelp {...props} />
      </HelpButton>
      <CloneButton {...props} />
      <BackButton {...props} />
    </TopToolbar>
  );
};

// Keeps Liquid Bulk visibility in sync with the live commodity selection so the
// TCF tab appears/disappears as the user changes the Load Type. Renders nothing.
const CommodityWatcher = ({ onCommodityChange }) => {
  const { values } = useFormState({ subscription: { values: true } });
  const commodityId = values?.reloadYardCommodityId;

  useEffect(() => {
    onCommodityChange(commodityId);
  }, [commodityId]);

  return null;
};

export const ProductEdit = (props) => {
  const history = useHistory();
  const notify = useNotify();
  const { record } = useEditController(props);
  const [account, setAccount] = useState(props.location?.state?.account);
  const [commodityId, setCommodityId] = useState(record?.reloadYardCommodityId);
  const backPath = getBackPath(props);

  useEffect(() => {
    if (!account && record?.accountId) {
      fetchAccount(record.accountId, setAccount, notify);
    }
  }, [account, record]);

  // CommodityWatcher (inside the form) keeps this in sync with both the loaded
  // record and live Load Type changes; the useState seed covers first render.
  const showTCFTab = getEnv() !== "prod" && isLiquidBulkCommodity(commodityId);

  return (
    <Edit
      {...props}
      title={<ForceTitle title="Edit Product" />}
      mutationMode="pessimistic"
      actions={
        <EditActions
          {...props}
          backPath={backPath}
          history={history}
          account={account}
        />
      }
      aside={
        <ProductEditAside
          {...props}
          backPath={backPath}
          history={history}
          account={account}
        />
      }
    >
      <TabbedForm
        variant="outlined"
        mutators={{ setFieldData }}
        toolbar={
          <EditToolbar
            {...props}
            record={record}
            backPath={backPath}
            history={history}
            account={account}
          />
        }
      >
        <FormTab label="Product Config">
          <CommodityWatcher onCommodityChange={setCommodityId} />
          <ProductForm account={account} />
        </FormTab>
        {showTCFTab && (
          <FormTab path="tcfTables" label="TCF Tables">
            <TCFTablesForm />
          </FormTab>
        )}
        <FormTab label="Agent Config">
          <AgentConfigForm record={record} />
        </FormTab>
        <FormTab path="historyLog" label="History Log">
          <HistoryTab />
        </FormTab>
      </TabbedForm>
    </Edit>
  );
};
